import { lazy, Suspense, Component, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom'
import { RedirectToCamp } from '@/pages/redirects'
import { Sentry } from '@/lib/sentry'
import { AuthProvider } from '@/store/AuthContext.tsx'
import { AppProvider } from '@/store/AppContext.tsx'
import { OrganizationProvider } from '@/store/OrganizationContext.tsx'
import { ThemeProvider } from '@/store/ThemeContext.tsx'
import { AiSidebarProvider } from '@/store/AiSidebarContext'
import { AuthGuard } from '@/components/layout/AuthGuard'
import { AppLayout } from '@/components/layout/AppLayout'

const Dashboard = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })))
const CampDetail = lazy(() => import('@/pages/CampDetail'))
const Partners = lazy(() => import('@/pages/Partners').then(m => ({ default: m.Partners })))
const Timeline = lazy(() => import('@/pages/Timeline').then(m => ({ default: m.Timeline })))
const Camps = lazy(() => import('@/pages/Camps').then(m => ({ default: m.Camps })))
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })))
const LandingEditor = lazy(() => import('@/pages/settings/LandingEditor'))
const Admin = lazy(() => import('@/pages/Admin').then(m => ({ default: m.Admin })))
const Login = lazy(() => import('@/pages/Login').then(m => ({ default: m.Login })))
const Forms = lazy(() => import('@/pages/Forms').then(m => ({ default: m.Forms })))
const FormBuilder = lazy(() => import('@/pages/FormBuilder'))
const FormInstances = lazy(() => import('@/pages/FormInstances'))
const FormResponses = lazy(() => import('@/pages/FormResponses'))
const PublicForm = lazy(() => import('@/pages/PublicForm'))
const PublicFormThanks = lazy(() => import('@/pages/PublicFormThanks'))
const PublicItinerary = lazy(() => import('@/pages/PublicItinerary'))
const PublicSettle = lazy(() => import('@/pages/PublicSettle'))
const PublicInquiry = lazy(() => import('@/pages/PublicInquiry'))
const Inquiries = lazy(() => import('@/pages/Inquiries').then(m => ({ default: m.Inquiries })))
const TenantPublicRoot = lazy(() => import('@/pages/tenant/TenantPublicRoot'))

// Hosts that should be treated as the apex (full app). Multi-level
// subdomains under any apex are also apex (e.g. blank.staging.atsume.camp
// is reserved for staging only when staging is in this list).
const APEX_HOSTS = (import.meta.env.VITE_APEX_HOSTS || 'atsume.camp,staging.atsume.camp')
  .split(',')
  .map((h: string) => h.trim())
  .filter(Boolean)

// Subdomain labels that must NOT be treated as a tenant slug. The DB
// validation should also enforce this on `organizations.slug`.
const RESERVED_SUBDOMAINS = new Set([
  'www', 'api', 'admin', 'staging', 'staging-preview', 'mail', 'app',
])

// Custom apex domains mapped to a tenant slug. Lets a tenant own a
// dedicated TLD without having to live under *.atsume.camp.
// Empty since blank.training was decoupled — it is now served by the
// standalone `atsume-group/blank` repo, not as an atsm-camp tenant.
const HOST_TO_SLUG: Record<string, string> = {}

// Full-host 302 redirects. Used when a tenant moves to a different
// canonical domain. Path + query are preserved.
const HOST_REDIRECTS: Record<string, string> = {}

function detectTenantSlug(): string | null {
  const host = window.location.hostname
  // Full-host redirect (domain rename) takes priority.
  const redirectHost = HOST_REDIRECTS[host]
  if (redirectHost) {
    const url = new URL(window.location.href)
    url.hostname = redirectHost
    window.location.replace(url.toString())
    return null
  }
  // Custom apex (e.g. blank.training) → known slug.
  if (HOST_TO_SLUG[host]) return HOST_TO_SLUG[host]
  // *.atsume.camp wildcard subdomain pattern.
  for (const apex of APEX_HOSTS) {
    if (host === apex) return null
    if (host.endsWith('.' + apex)) {
      const sub = host.slice(0, -('.' + apex).length)
      if (sub.includes('.')) return null // multi-level → treat as apex
      if (RESERVED_SUBDOMAINS.has(sub)) return null
      return sub
    }
  }
  // Local dev / preview fallback: ?tenant=blank-schedule
  const params = new URLSearchParams(window.location.search)
  return params.get('tenant')
}

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

function NotFound() {
  const { t } = useTranslation()
  return (
    <div className="flex h-screen items-center justify-center bg-bg">
      <div className="text-center">
        <p className="text-6xl font-bold text-text-3">404</p>
        <p className="mt-2 text-lg text-text-2">{t('misc.app.notFound')}</p>
        <Link to="/" className="mt-4 inline-block text-sm text-accent hover:underline">
          {t('misc.app.backToDashboard')}
        </Link>
      </div>
    </div>
  )
}

function ErrorFallback({ error }: { error: Error }) {
  const { t } = useTranslation()
  return (
    <div className="flex h-screen items-center justify-center bg-bg">
      <div className="text-center">
        <p className="text-lg font-medium text-red-400">{t('misc.app.unexpectedError')}</p>
        <p className="mt-2 text-sm text-text-3">{error.message}</p>
        <button onClick={() => window.location.reload()} className="mt-4 text-sm text-accent hover:underline">
          {t('misc.app.reload')}
        </button>
      </div>
    </div>
  )
}

// Recognise the family of errors that happen when an old client HTML
// references hashed asset chunks from a prior deploy. Force-reload once
// (sessionStorage-guarded) so the user transparently lands on the new
// build. If reload itself fails to recover, fall through to the
// ErrorFallback.
const CHUNK_RELOAD_KEY = '__chunk_reload__'
function isChunkLoadError(message: string): boolean {
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Unable to preload (?:CSS|module|script)/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    /Importing a module script failed/i.test(message)
  )
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isChunkLoadError(error.message)) {
      // Skip Sentry noise for the well-known stale-HTML case.
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
        const url = new URL(window.location.href)
        url.searchParams.set('_r', String(Date.now()))
        window.location.replace(url.toString())
      }
      return
    }
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
  }
  render() {
    if (this.state.error) {
      // Show a soft loader while the reload is in flight so the user
      // doesn't see the error UI flash.
      if (isChunkLoadError(this.state.error.message)) {
        return <PageLoader />
      }
      return <ErrorFallback error={this.state.error} />
    }
    return this.props.children
  }
}

export default function App() {
  const tenantSlug = detectTenantSlug()
  if (tenantSlug) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <Suspense fallback={<PageLoader />}>
            <TenantPublicRoot slug={tenantSlug} />
          </Suspense>
        </ThemeProvider>
      </ErrorBoundary>
    )
  }
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <AiSidebarProvider>
      <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                {/* Public form pages — no auth required */}
                <Route path="/f/:token" element={<PublicForm />} />
                <Route path="/f/:slug/:token" element={<PublicForm />} />
                <Route path="/f/:token/thanks" element={<PublicFormThanks />} />
                <Route path="/f/:slug/:token/thanks" element={<PublicFormThanks />} />
                {/* 旅程の読み取り専用リンク — no auth required */}
                <Route path="/s/:token" element={<PublicItinerary />} />
                <Route path="/p/:token" element={<PublicSettle />} />
                {/* Public inquiry intake (camp-less) — no auth required */}
                <Route path="/q/:orgSlug" element={<PublicInquiry />} />
                <Route path="/q/:orgSlug/thanks" element={<PublicFormThanks />} />
                <Route
                  element={
                    <AuthGuard>
                      <OrganizationProvider>
                        <AppProvider>
                          <AppLayout />
                        </AppProvider>
                      </OrganizationProvider>
                    </AuthGuard>
                  }
                >
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/camps" element={<Camps />} />
                  <Route path="/camps/:campId" element={<CampDetail />} />
                  {/* Legacy URL compat — 301-equivalent SPA redirects */}
                  <Route path="/trips/:campId" element={<RedirectToCamp />} />
                  <Route path="/tours/:campId" element={<RedirectToCamp />} />
                  <Route path="/tours" element={<Navigate to="/camps" replace />} />
                  <Route path="/partners" element={<Partners />} />
                  <Route path="/timeline" element={<Timeline />} />
                  <Route path="/inquiries" element={<Inquiries />} />
                  <Route path="/forms" element={<Forms />} />
                  <Route path="/forms/:id" element={<FormBuilder />} />
                  <Route path="/forms/:id/instances" element={<FormInstances />} />
                  <Route path="/forms/:id/instances/:instanceId/responses" element={<FormResponses />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/landing" element={<LandingEditor />} />
                  <Route path="/admin" element={<Admin />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
      </AuthProvider>
      </AiSidebarProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
